import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const API_URL = 'https://nings-api.beamer-labs.workers.dev';

interface AuthVerifyProps {
  login: (accessToken: string, refreshToken: string, userData: any) => void;
}

const AuthVerify: React.FC<AuthVerifyProps> = ({ login }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [message, setMessage] = useState('Verifying magic link...');
  const [error, setError] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const preAuthSessionId = params.get('preAuthSessionId');
    const deviceId = params.get('deviceId');
    const linkCode = params.get('linkCode');
    const userInputCode = params.get('userInputCode');

    // Check if we have the required parameters
    if (!preAuthSessionId || !deviceId || (!linkCode && !userInputCode)) {
      setError('Invalid magic link. Missing required parameters.');
      setMessage('Verification failed.');
      return;
    }

    const consumePasswordlessCode = async () => {
      try {
        setMessage('Signing you in...');

        // Build the query parameters
        const consumeParams = new URLSearchParams({
          preAuthSessionId,
          deviceId
        });

        if (linkCode) {
          consumeParams.append('linkCode', linkCode);
        } else if (userInputCode) {
          consumeParams.append('userInputCode', userInputCode);
        }

        const response = await fetch(
          `${API_URL}/auth/passwordless/consume?${consumeParams.toString()}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            }
          }
        );

        const data = await response.json();

        if (response.ok && data.status === 'OK') {
          // Store tokens and user data
          login(
            data.accessToken.token,
            data.refreshToken.token,
            data.user
          );

          setMessage('Success! Redirecting...');

          // Check for saved redirect params (e.g., ?ticker=AAPL)
          // Using localStorage instead of sessionStorage because magic links open in new tabs
          const savedParams = localStorage.getItem('nings_redirect_params');
          if (savedParams) {
            localStorage.removeItem('nings_redirect_params');
            navigate(`/${savedParams}`);
          } else {
            navigate('/');
          }
        } else {
          setError(data.detail || 'Magic link verification failed.');
          setMessage('Verification failed.');
        }
      } catch (err: any) {
        console.error('Magic link verification error:', err);
        setError(err.message || 'An unexpected error occurred during verification.');
        setMessage('Verification failed.');
      }
    };

    consumePasswordlessCode();
  }, [location, navigate, login]);

  return (
    <div style={{ padding: '20px', textAlign: 'center' }}>
      <h1>Magic Link Verification</h1>
      {message && <p>{message}</p>}
      {error && <p style={{ color: 'red' }}>Error: {error}</p>}
    </div>
  );
};

export default AuthVerify;
